import {Box, Typography} from '@mui/material';
import {t} from 'i18next';
import './SummarizeHeader.scss';
import ArrButton from '../../../common/arr-button/ArrButton';
import ArrIconButton from '../../../common/arr-icon-button/ArrIconButton';
import {ReactComponent as DownloadIcon} from '../../../../assets/images/download_icon.svg';
import {ReactComponent as DownloadDisabledIcon} from '../../../../assets/images/icon-download-disabled.svg';
import {useState} from 'react';
import CreateNewJobFlyout from '../create-new-job-flyout/CreateNewJobFlyout';
import ArrToastNotification from '../../../common/arr-toast-notification/ArrToastNotification';
import {ToastType} from '../../../../shared/constants';
import {getCsvDownload} from '../../../../api/endpoints/aiSummarizationApi';
import {UseSummarizedGrid} from '../data-grid/SummarizeDataGridController';
import ArrTooltipWrapper from '../../../common/arr-tooltip-wrapper/ArrTooltipWrapper';
import {ReactComponent as InfoIcon} from '../../../../assets/images/info-icon.svg';

const SummarizeHeader = () => {
  const [isFlyoutOpen, setIsFlyoutOpen] = useState<boolean>(false);
  const [showToast, setShowToast] = useState<boolean>(false);
  const [showErrorToast, setShowErrorToast] = useState<boolean>(false);
  const [openToast, setOpenToast] = useState<boolean>(false);
  const [toastMsg, setToastMsg] = useState<string>('');
  const [toastStatus, setToastStatus] = useState<ToastType[]>([]);
  const {summarizeJobData} = UseSummarizedGrid();

  const triggerToast = (message: string, status: ToastType[]) => {
    setToastMsg(message);
    setToastStatus(status);
    setOpenToast(true);
  };

  const handleDownload = async () => {
    try {
      const response = await getCsvDownload();
      const {blob, filename} = response;

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click(); // Trigger the download
      window.URL.revokeObjectURL(url); // Clean up the URL object
    } catch (error) {
      console.error('Download failed', error);
      setShowErrorToast(true);
    }
  };

  return (
    <Box className="summarize-header">
      <Typography
        className="typo-text headline3_size"
        tabIndex={0}
        aria-label={t('ai.summarize.header.title')}
        data-testid={`summarize-header-title`}
      >
        {t('ai.summarize.header.title')}&nbsp;
        <ArrTooltipWrapper
          trigger={
            <InfoIcon
              className="info-icon-label"
              data-testid="toggle-info-tooltip"
              tabIndex={0}
              aria-label='info-icon'
            />
          }
          title={t('ai.summarize.header.title')}
          closeTooltipOnEscPress
        >
          <p tabIndex={0}> {t('ai.summarize.header.tooltip-info')}</p>
          <p tabIndex={0}>{t('ai.summarize.header.tooltip-note')}</p>
        </ArrTooltipWrapper>
      </Typography>

      <Box className="summarize-header-action">
        <ArrIconButton
          icon={
            (summarizeJobData?.jobDetailsDtos?.length ?? 0) > 0 ? (
              <DownloadIcon />
            ) : (
              <DownloadDisabledIcon className="download-icon-disabled" />
            )
          }
          ariaLabel={'download-icon'}
          disabled={!((summarizeJobData?.jobDetailsDtos?.length ?? 0) > 0)}
          className={
            !((summarizeJobData?.jobDetailsDtos?.length ?? 0) > 0)
              ? 'download-disabled-container'
              : undefined
          }
          onClick={handleDownload}
          testId="summarize-download-icon"
        ></ArrIconButton>
        {isFlyoutOpen && (
          <CreateNewJobFlyout
            isOpen={isFlyoutOpen}
            onClose={() => setIsFlyoutOpen(false)}
            setShowToast={setShowToast}
            triggerToast={triggerToast}
          />
        )}
        <ArrButton
          variant="primary"
          size="medium"
          onClick={() => setIsFlyoutOpen(true)}
          disabled={false}
          aria-label={t('ai.summarize.header.newJob')}
          dataTestId="create-new-job"
          className="new-job-button"
        >
          {t('ai.summarize.header.newJob')}
        </ArrButton>
        {showToast && (
          <ArrToastNotification
            message={t('ai.summarize.createJob.successMessage')}
            status={[ToastType.Success]}
            onClose={() => setShowToast(false)}
          />
        )}
        {showErrorToast && (
          <ArrToastNotification
            message={t('ai.summarize.download.failed')}
            onClose={() => setShowErrorToast(false)}
            status={[ToastType.Error]}
          />
        )}
        {openToast && (
          <ArrToastNotification
            message={toastMsg}
            onClose={() => setOpenToast(false)}
            status={toastStatus}
          />
        )}
      </Box>
    </Box>
  );
};

export default SummarizeHeader;
